import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Target, Copy, Check, RefreshCcw, ExternalLink, TrendingUp, AlertCircle,
  Send, Loader2, X, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { days: 7,  label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

function AdminUpgradeCandidates() {
  const [data, setData] = useState({
    candidates: [], window_days: 30, qualifying_partners: 0, total_partners: 0,
    cooldown_days: 14, from_email: '', reply_to: '',
  });
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [minClicks, setMinClicks] = useState(10);
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(null);
  const [openSlug, setOpenSlug] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { candidate, variant }
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/upgrade-candidates`, {
        params: { days: windowDays, min_clicks: minClicks },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({
        candidates: res.data?.candidates || [],
        window_days: res.data?.window_days || windowDays,
        qualifying_partners: res.data?.qualifying_partners || 0,
        total_partners: res.data?.total_partners || 0,
        cooldown_days: res.data?.cooldown_days || 14,
        from_email: res.data?.from_email || '',
        reply_to: res.data?.reply_to || '',
      });
    } catch {
      toast.error('Failed to load upgrade candidates');
    } finally {
      setLoading(false);
    }
  }, [windowDays, minClicks]);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, key, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const sendPitch = async (toEmail) => {
    if (!sendModal) return;
    const { candidate, variant } = sendModal;
    try {
      setSending(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/upgrade-candidates/${candidate.slug}/send`,
        { to_email: toEmail, subject_variant: variant },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Pitch sent to ${toEmail}`);
      setSendModal(null);
      load(); // refresh to show last_contacted badge
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Send failed';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const visibleCandidates = useMemo(() => {
    if (showAll) return data.candidates;
    return data.candidates.filter((c) => c.qualifies);
  }, [data.candidates, showAll]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-upgrade-candidates-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="text-2xl font-bold text-white">Upgrade Candidates</h1>
            </div>
            <p className="text-sm text-white/60 max-w-2xl">
              Existing affiliate partners ranked by real click volume from <code className="text-[#00F0FF] text-xs">/featured</code>.
              Each row has a paste-ready (or one-click <strong>Send via Resend</strong>) cold-pitch email with their actual click number baked in — the workflow for upselling
              them into the paid <strong>$99/mo Featured Platform Partner</strong> slot.
            </p>
            {data.from_email && (
              <p className="text-xs text-white/40 mt-1.5">
                Sends from <code className="text-[#00F0FF]">{data.from_email}</code>
                {data.reply_to && data.reply_to !== data.from_email && (
                  <> · replies to <code className="text-[#00F0FF]">{data.reply_to}</code></>
                )}
                {' · '}{data.cooldown_days}-day cooldown enforced per partner
              </p>
            )}
          </div>
          <Button
            variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="upgrade-candidates-refresh-btn"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <span className="text-xs uppercase tracking-wider font-bold text-white/60">Window</span>
          <div className="flex gap-1.5">
            {WINDOW_OPTIONS.map(({ days, label }) => (
              <button
                key={days} type="button"
                data-testid={`window-${days}d-btn`}
                onClick={() => setWindowDays(days)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  windowDays === days
                    ? 'bg-[#00F0FF] text-black'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <span className="ml-4 text-xs uppercase tracking-wider font-bold text-white/60">Min clicks to qualify</span>
          <input
            type="number" min={0} max={10000} value={minClicks}
            data-testid="min-clicks-input"
            onChange={(e) => setMinClicks(Math.max(0, parseInt(e.target.value || '0', 10)))}
            className="w-20 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-sm text-white"
          />

          <label className="ml-auto inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none">
            <input
              type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
              data-testid="show-all-checkbox"
              className="accent-[#00F0FF]"
            />
            Show non-qualifying partners
          </label>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label={`Qualifying partners (≥${minClicks} clicks/${windowDays}d)`} value={data.qualifying_partners} testid="stat-qualifying" />
          <Stat label="Total active partners" value={data.total_partners} testid="stat-total" />
          <Stat
            label="Top partner this window"
            value={data.candidates[0]?.name || '—'}
            sub={data.candidates[0] ? `${data.candidates[0].clicks_window} clicks` : ''}
            testid="stat-top"
          />
        </div>

        {/* Candidates list */}
        {loading && data.candidates.length === 0 ? (
          <div className="rounded-xl border border-white/10 p-8 text-center text-white/50">Loading…</div>
        ) : visibleCandidates.length === 0 ? (
          <EmptyState minClicks={minClicks} windowDays={windowDays} onShowAll={() => setShowAll(true)} />
        ) : (
          <div className="space-y-3" data-testid="candidates-list">
            {visibleCandidates.map((c) => (
              <CandidateRow
                key={c.slug}
                candidate={c}
                isOpen={openSlug === c.slug}
                onToggle={() => setOpenSlug(openSlug === c.slug ? null : c.slug)}
                copied={copied}
                onCopy={copy}
                onSend={(variant) => setSendModal({ candidate: c, variant })}
                cooldownDays={data.cooldown_days}
              />
            ))}
          </div>
        )}
      </div>

      {/* Send modal */}
      {sendModal && (
        <SendModal
          candidate={sendModal.candidate}
          variant={sendModal.variant}
          fromEmail={data.from_email}
          replyTo={data.reply_to}
          sending={sending}
          onCancel={() => setSendModal(null)}
          onSend={sendPitch}
        />
      )}
    </AdminLayout>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-bold text-white/60">{label}</div>
      <div className="text-2xl font-bold text-white mt-1 truncate">{value}</div>
      {sub && <div className="text-xs text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyState({ minClicks, windowDays, onShowAll }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 p-8 text-center" data-testid="empty-state">
      <AlertCircle className="h-8 w-8 text-white/40 mx-auto mb-3" />
      <p className="text-sm text-white/70 mb-1">
        No partners cleared the <strong>{minClicks} clicks / {windowDays}d</strong> bar yet.
      </p>
      <p className="text-xs text-white/50 mb-4">
        Either lower the minimum, widen the window, or wait for more click volume to accrue.
      </p>
      <Button
        variant="outline" size="sm" onClick={onShowAll}
        data-testid="empty-show-all-btn"
        className="border-white/20 text-white hover:bg-white/10"
      >
        Show all partners anyway
      </Button>
    </div>
  );
}

function CandidateRow({ candidate, isOpen, onToggle, copied, onCopy, onSend, cooldownDays }) {
  const c = candidate;
  const k = (suffix) => `${c.slug}-${suffix}`;
  const subjAndBody = (subject) => `Subject: ${subject}\n\n${c.pitch.body}`;
  const cooldownActive = c.in_cooldown;

  return (
    <article
      className={`rounded-xl border bg-white/[0.03] overflow-hidden ${
        c.qualifies ? 'border-white/10' : 'border-white/5 opacity-60'
      }`}
      data-testid={`candidate-${c.slug}`}
    >
      {/* Summary row */}
      <button
        type="button" onClick={onToggle}
        data-testid={`candidate-toggle-${c.slug}`}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/5"
      >
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            c.rank <= 3 ? 'bg-[#00F0FF] text-black' : 'bg-white/10 text-white/70'
          }`}>
            {c.rank}
          </div>
          {c.logo_url ? (
            <img
              src={c.logo_url} alt={c.name}
              className="w-10 h-10 rounded-lg object-contain bg-white/5 border border-white/10 p-1 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-white truncate">{c.name}</h3>
              {c.qualifies && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/30">
                  Qualifies
                </span>
              )}
              {cooldownActive && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30"
                  data-testid={`cooldown-badge-${c.slug}`}
                >
                  <Clock className="h-3 w-3" />
                  Last contacted {c.days_since_last_contact}d ago
                </span>
              )}
            </div>
            <p className="text-xs text-white/50 truncate">
              {c.category || '—'} · {c.website_display || c.slug}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="text-right">
            <div className="text-lg font-bold text-white tabular-nums">{c.clicks_window}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50">window</div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-lg font-bold text-white/70 tabular-nums">{c.clicks_all_time}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50">all-time</div>
          </div>
        </div>
      </button>

      {/* Expanded pitch */}
      {isOpen && (
        <div className="px-4 pb-5 pt-1 space-y-4 border-t border-white/5">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <a
              href={c.listing_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-white/70 hover:text-white px-2.5 py-1 rounded border border-white/15"
              data-testid={`candidate-listing-link-${c.slug}`}
            >
              <ExternalLink className="h-3 w-3" /> View public listing
            </a>
            {c.affiliate_url && (
              <a
                href={c.affiliate_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-white/70 hover:text-white px-2.5 py-1 rounded border border-white/15"
                data-testid={`candidate-affiliate-link-${c.slug}`}
              >
                <TrendingUp className="h-3 w-3" /> Affiliate URL
              </a>
            )}
            {cooldownActive && (
              <span className="text-amber-300/90 text-[11px]">
                ⏱ Send disabled for {cooldownDays - c.days_since_last_contact} more day(s) (last sent to {c.last_contacted_to})
              </span>
            )}
          </div>

          <SubjectBlock
            label="Subject A" value={c.pitch.subject_a}
            testid={`subj-a-${c.slug}`}
            copied={copied === k('subj-a')}
            onCopy={() => onCopy(c.pitch.subject_a, k('subj-a'), 'Subject A copied')}
          />
          <SubjectBlock
            label="Subject B" value={c.pitch.subject_b}
            testid={`subj-b-${c.slug}`}
            copied={copied === k('subj-b')}
            onCopy={() => onCopy(c.pitch.subject_b, k('subj-b'), 'Subject B copied')}
          />

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider font-bold text-white/60">Body</span>
              <button
                type="button"
                onClick={() => onCopy(c.pitch.body, k('body'), 'Body copied')}
                data-testid={`candidate-copy-body-${c.slug}`}
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
              >
                {copied === k('body')
                  ? <><Check className="h-3 w-3 text-[#00FF94]" />copied</>
                  : <><Copy className="h-3 w-3" />copy body</>}
              </button>
            </div>
            <pre className="bg-black/40 border border-white/10 rounded-lg p-4 whitespace-pre-wrap font-sans text-sm text-white/85 leading-relaxed">
{c.pitch.body}
            </pre>
          </div>

          {/* Action row: copy + send buttons for each variant */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              type="button"
              onClick={() => onCopy(subjAndBody(c.pitch.subject_a), k('combo-a'), 'Subject A + body copied')}
              data-testid={`candidate-copy-combo-a-${c.slug}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/15 hover:bg-white/15"
            >
              {copied === k('combo-a')
                ? <><Check className="h-3 w-3 text-[#00FF94]" />copied · A</>
                : <><Copy className="h-3 w-3" />Copy A</>}
            </button>
            <button
              type="button"
              onClick={() => onCopy(subjAndBody(c.pitch.subject_b), k('combo-b'), 'Subject B + body copied')}
              data-testid={`candidate-copy-combo-b-${c.slug}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/15 hover:bg-white/15"
            >
              {copied === k('combo-b')
                ? <><Check className="h-3 w-3 text-[#00FF94]" />copied · B</>
                : <><Copy className="h-3 w-3" />Copy B</>}
            </button>

            <span className="mx-2 text-white/20">|</span>

            <button
              type="button"
              disabled={cooldownActive}
              onClick={() => onSend('a')}
              data-testid={`candidate-send-a-${c.slug}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#00F0FF] text-black font-semibold hover:bg-[#00F0FF]/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send className="h-3 w-3" />Send via Resend (A)
            </button>
            <button
              type="button"
              disabled={cooldownActive}
              onClick={() => onSend('b')}
              data-testid={`candidate-send-b-${c.slug}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send className="h-3 w-3" />Send via Resend (B)
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function SubjectBlock({ label, value, testid, copied, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wider font-bold text-white/60">{label}</span>
        <button
          type="button" onClick={onCopy} data-testid={testid}
          className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
        >
          {copied
            ? <><Check className="h-3 w-3 text-[#00FF94]" />copied</>
            : <><Copy className="h-3 w-3" />copy</>}
        </button>
      </div>
      <p className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white/85 font-mono break-words">
        {value}
      </p>
    </div>
  );
}

function SendModal({ candidate, variant, fromEmail, replyTo, sending, onCancel, onSend }) {
  const [email, setEmail] = useState(candidate.outreach_email || '');
  const [confirmed, setConfirmed] = useState(false);
  const subject = variant === 'a' ? candidate.pitch.subject_a : candidate.pitch.subject_b;
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="send-modal"
      onClick={onCancel}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button" onClick={onCancel}
          data-testid="send-modal-close"
          className="absolute top-3 right-3 text-white/50 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-bold text-white mb-1">
          Send pitch to {candidate.name}
        </h2>
        <p className="text-xs text-white/50 mb-4">
          Variant {variant.toUpperCase()} · from {fromEmail}{replyTo && replyTo !== fromEmail ? ` · replies to ${replyTo}` : ''}
        </p>

        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-white/60 mb-1.5">
              Recipient email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              placeholder={`partnerships@${candidate.website_display || 'partner.com'}`}
              data-testid="send-modal-email-input"
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30"
              autoFocus
            />
            {candidate.outreach_email && (
              <p className="text-[11px] text-white/40 mt-1">
                Saved from previous send. Clear to use a different address.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-white/60 mb-1.5">
              Subject
            </label>
            <p className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white/85 font-mono break-words">
              {subject}
            </p>
          </div>

          <details className="text-xs text-white/60">
            <summary className="cursor-pointer hover:text-white/80 select-none">
              Preview body
            </summary>
            <pre className="mt-2 max-h-60 overflow-y-auto bg-black/40 border border-white/10 rounded-lg p-3 whitespace-pre-wrap font-sans text-xs text-white/75 leading-relaxed">
{candidate.pitch.body}
            </pre>
          </details>

          <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer select-none">
            <input
              type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="send-modal-confirm-checkbox"
              className="accent-[#00F0FF] mt-0.5"
            />
            <span>
              I&apos;ve verified the recipient is the right contact and this email is a legitimate
              B2B partnership pitch. (A 14-day cooldown will apply per partner after sending.)
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline" size="sm" onClick={onCancel} disabled={sending}
            data-testid="send-modal-cancel-btn"
            className="border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onSend(email)}
            disabled={!isValid || !confirmed || sending}
            data-testid="send-modal-confirm-btn"
            className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
          >
            {sending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</> : <><Send className="h-3.5 w-3.5 mr-1.5" />Send now</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminUpgradeCandidates;
